import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './MyActivityInfo.module.css';

import { analysisApiSlice } from '../../../services/analysis';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import {
  selectMySummary,
  mySummaryChanged,
  myProjectListChanged,
  myProjectIdChanged,
  selectMyProjectId,
  studentUserIdChanged,
  selectMyProjectLibrary,
  myProjectLibraryChanged,
} from '../../../features/analysisSlice';
import { SummaryDuration, Summary } from '../../../types/analysis';
import {
  CARD_CONFIGS,
  CLASS_AVERAGE_LEARNING_TIME,
  CONTENT_LABELS,
  DAILY_BOOKS_LEARNED_DETAILS,
  DAILY_LEARNING_BOOKS,
  DELETE_ALL_ACTIVITY,
  LEARNING_ACTIVITY_SUFFIX,
  LEARNING_CONTENTS,
  LEARNING_TOOL_LABELS,
  LEARNING_TOOLS,
  MAX_CONCENTRATION_TIME,
  MAX_CONCENTRATION_TIME_DETAILS,
  NOTE_USAGE_TIME,
  NOTE_USAGE_TIME_DETAILS,
  NOTE_USAGE_TIME_DETAILS_IN_TITLE,
  PRIMARY_STUDY_DAYS,
  PRIMARY_STUDY_HOURS,
  SELPUB_STUDENTS_AVERAGE_CONCENTRATION_TIME,
  SELPUB_STUDENTS_DAILY_BOOKS_LEARNED,
  TOTAL_STUDY_TIME,
  USER_LOGIN_ANALYSIS,
  VOLUME,
} from '../../../constants';
import useModal from '../../../hooks/useModal';
import Library from '../Library';
import {
  FullDashboard,
  LastStudyPanel,
  LastAttendancePanel,
  KeywordPanel,
  HorizontalBarPanel,
  VerticalBarPanel,
  PlainInfoPanel,
} from '../../../components/Dashboard';
import { PanelType, PanelConfig } from '../../../types/dashboard';
import { formatCount, makeCard } from '../../../utils/dashboard';

export default function MyActivityInfo() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const { toggleModal } = useModal();

  const summary = useAppSelector(selectMySummary);
  const projectId = useAppSelector(selectMyProjectId);
  const libraryType = useAppSelector(selectMyProjectLibrary);

  const [getSummary] = analysisApiSlice.useLazyGetSummaryQuery();
  const [getProjects, { isSuccess }] =
    analysisApiSlice.useLazyGetProjectListQuery();

  const [summaryDuration, setSummaryDuration] = useState<SummaryDuration>();
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const deleteAllActivity = () => {
    toggleModal({
      type: 'confirm',
      id: 'confirmDeleteAllActivity',
      isOpen: true,
    });
  };

  const changeSummaryDate = async (summaryDuration?: SummaryDuration) => {
    try {
      const { data } = await getSummary(summaryDuration);
      dispatch(mySummaryChanged(data));
    } catch (error) {
      console.error('Fail to get Summary', summaryDuration, error);
    }
  };

  useEffect(() => {
    changeSummaryDate(summaryDuration);
  }, [summaryDuration]);

  useEffect(() => {
    dispatch(studentUserIdChanged(0));
  }, []);

  if (!summary) return null;

  const {
    alias = '',
    regDate,
    today,
    total,
    timeZone,
    weekZone,
    daily,
    attention,
    note,
    lastStudy,
    lastAttendance,
    keyword,
    annotation,
    content,
  } = summary as Summary;
  const { startDate = '', endDate = '', totalTime } = total;

  const cardValues: Record<string, { value: string; coverImage?: string }> = {
    'last-learning': {
      value: lastStudy?.title || '-',
      coverImage: lastStudy?.coverFilePath,
    },
    'last-date': { value: lastAttendance?.lastDate || '-' },
    keyword: {
      value:
        keyword && keyword.keywordList.length
          ? keyword.keywordList[0].keyword
          : '-',
    },
    tool: { value: formatCount(annotation?.totalCount) },
    content: { value: formatCount(content?.totalCount) },
    'total-time': { value: totalTime || '-' },
    'time-range': { value: timeZone.best || '-' },
    day: { value: weekZone.best || '-' },
    book: { value: daily.avg ? `${daily.avg}${VOLUME}` : '-' },
    focus: { value: attention.time || '-' },
    note: { value: note.time || '-' },
  };

  const activityCards = CARD_CONFIGS.map(({ id, title }) => {
    const { value, coverImage } = cardValues[id];
    return makeCard(id, title, value, coverImage);
  });

  const dashboardTitle = LEARNING_ACTIVITY_SUFFIX.replace('{alias}', alias);

  const dateRange = {
    startDate,
    endDate,
    regDate,
    today,
  };

  const handleCardClick = (cardId: string) => {
    const card = activityCards.find((card) => card.id === cardId);
    if (card && card.value !== '-') {
      setActivePanel(cardId);
    }
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    setSummaryDuration({ startDate, endDate });
  };

  const handleClosePanel = () => {
    setActivePanel(null);
  };

  const actionButton = {
    text: DELETE_ALL_ACTIVITY,
    onClick: deleteAllActivity,
    className: 'del_activity',
  };

  const panelConfigs: PanelConfig[] = [
    {
      id: PanelType.LAST_STUDY,
      Component: LastStudyPanel,
      condition: lastStudy?.status !== 4,
      props: {
        data: {
          bookTitle: lastStudy?.title || '',
          bookCover: lastStudy?.coverFilePath,
          page: lastStudy?.page || 0,
          status: lastStudy?.status || 0,
          description: lastStudy?.summary,
        },
      },
    },
    {
      id: PanelType.LAST_ATTENDANCE,
      Component: LastAttendancePanel,
      condition: !!lastAttendance?.lastDate,
      props: {
        data: {
          lastDate: lastAttendance?.lastDate || '',
          lastWeek: lastAttendance?.lastWeek || '',
          attendedDates: lastAttendance?.dateList || [],
        },
      },
    },
    {
      id: PanelType.KEYWORD,
      Component: KeywordPanel,
      condition: keyword?.status === 1,
      props: {
        data:
          keyword?.keywordList.slice(0, 5).map((item) => ({
            rank: item.order,
            keyword: item.keyword,
          })) || [],
      },
    },
    {
      id: PanelType.TOOL,
      Component: HorizontalBarPanel,
      condition: !!annotation?.totalCount,
      props: {
        cardId: 'tool',
        title: LEARNING_TOOLS,
        data:
          annotation?.typeList.slice(0, 3).map((item) => ({
            rank: item.order,
            name: item.dispType,
            value: item.count,
          })) || [],
        totalCount: annotation?.totalCount,
        topItemName: annotation?.typeList.find((item) => item.order === 1)
          ?.dispType,
        nameLabels: LEARNING_TOOL_LABELS,
      },
    },
    {
      id: PanelType.CONTENT,
      Component: HorizontalBarPanel,
      condition: !!content?.totalCount,
      props: {
        cardId: 'content',
        title: LEARNING_CONTENTS,
        data:
          content?.typeList.slice(0, 3).map((item) => ({
            rank: item.order,
            name: item.dispType,
            value: item.count,
          })) || [],
        totalCount: content?.totalCount,
        topItemName: content?.typeList.find((item) => item.order === 1)
          ?.dispType,
        nameLabels: CONTENT_LABELS,
      },
    },
    {
      id: PanelType.TOTAL_TIME,
      Component: PlainInfoPanel,
      condition: !!total.totalTime,
      props: {
        cardId: 'total-time',
        title: TOTAL_STUDY_TIME,
        data: {
          startDate,
          endDate,
          mainValue: totalTime,
          description: CLASS_AVERAGE_LEARNING_TIME.replace('{time}', totalTime),
          subtext: USER_LOGIN_ANALYSIS,
        },
      },
    },
    {
      id: PanelType.TIME_RANGE,
      Component: VerticalBarPanel,
      condition: !!timeZone.best,
      props: {
        cardId: 'time-range',
        title: PRIMARY_STUDY_HOURS,
        data:
          timeZone.timeZone.map((item) => ({
            name: item.title,
            value: item.value,
          })) || [],
        topItemName: timeZone.best,
      },
    },
    {
      id: PanelType.DAY,
      Component: VerticalBarPanel,
      condition: !!weekZone.best,
      props: {
        cardId: 'day',
        title: PRIMARY_STUDY_DAYS,
        data:
          weekZone.timeZone.map((item) => ({
            name: item.title,
            value: item.value,
          })) || [],
        topItemName: weekZone.best,
      },
    },
    {
      id: PanelType.BOOK,
      Component: PlainInfoPanel,
      condition: !!daily.avg,
      props: {
        cardId: 'book',
        title: DAILY_LEARNING_BOOKS,
        data: {
          mainValue: `${daily.avg}${VOLUME}`,
          min: daily.min,
          max: daily.max,
          avg: daily.avg,
          description: DAILY_BOOKS_LEARNED_DETAILS.replace(
            '{avg}',
            `${daily.avg}`
          ),
          subtext: SELPUB_STUDENTS_DAILY_BOOKS_LEARNED.replace(
            '{N}',
            `${daily.totalAvg}`
          ),
        },
      },
    },
    {
      id: PanelType.FOCUS,
      Component: PlainInfoPanel,
      condition: !!attention.time,
      props: {
        cardId: 'focus',
        title: MAX_CONCENTRATION_TIME,
        data: {
          mainValue: attention.time,
          total: attention.total,
          description: MAX_CONCENTRATION_TIME_DETAILS.replace(
            '{time}',
            attention.time
          ),
          subtext: SELPUB_STUDENTS_AVERAGE_CONCENTRATION_TIME.replace(
            '{totalAvg}',
            attention.totalAvg
          ),
        },
      },
    },
    {
      id: PanelType.NOTE,
      Component: PlainInfoPanel,
      condition: !!note.time,
      props: {
        cardId: 'note',
        title: NOTE_USAGE_TIME,
        data: {
          mainValue: note.time,
          total: note.total,
          description: NOTE_USAGE_TIME_DETAILS.replace('{time}', note.time),
          subtext: NOTE_USAGE_TIME_DETAILS_IN_TITLE.replace(
            '{title}',
            note.title
          ),
        },
      },
    },
  ];

  return (
    <>
      <FullDashboard
        title={dashboardTitle}
        cards={activityCards}
        dateRange={dateRange}
        actionButton={actionButton}
        onCardClick={handleCardClick}
        onDateChange={handleDateChange}
      >
        {activePanel &&
          panelConfigs.map(({ id, Component, condition, props }) =>
            condition && activePanel === id ? (
              <Component
                key={id}
                {...props}
                isOpen={true}
                onClose={handleClosePanel}
              />
            ) : null
          )}
      </FullDashboard>

      <Library
        type={libraryType}
        projects={{
          queryTrigger: getProjects,
          isSuccess,
          projectLibraryChanged: myProjectLibraryChanged,
          projectListChanged: myProjectListChanged,
          projectIdChanged: myProjectIdChanged,
        }}
        projectId={projectId}
      />
    </>
  );
}
